import { get, map, maxBy } from "lodash";
import { PROCESS_TAGS, SUBSCRIPTION_STATUSES } from "utils/constants";

export const SIGNED_LIKE_PROCESS_TAGS = [
  PROCESS_TAGS.SIGNED,
  PROCESS_TAGS.SIGNED_INCOMPLETE,
  PROCESS_TAGS.SENT_TO_MANAGEMENT,
];

export const getLatestStatusEntry = (statuses = []) =>
  maxBy(
    statuses.filter(({ status }) => status?.id),
    ({ status }) => parseInt(status.order, 10) || 0,
  );

export const getLatestProjectStatus = (statuses = []) =>
  get(getLatestStatusEntry(statuses), "status", null);

export const matchStatusesWithProcess = (statuses = [], tracers = []) => {
  const result = map(
    statuses.filter(({ status }) => status),
    ({ status: { id: statusId, order } }) => {
      const allProcessStatuses = tracers.flatMap(
        (tracer) =>
          tracer?.flow?.process?.processStatuses
            ?.filter(({ id }) => id === statusId)
            .map((status) => ({
              ...status,
              tracer,
            })) || [],
      );
      const matchedStatus = maxBy(allProcessStatuses, (candidate) => {
        return -Math.abs(candidate.order - order);
      });

      return {
        tracer: matchedStatus?.tracer || null,
        processStatus: matchedStatus || null,
      };
    },
  ).filter(({ tracer }) => tracer !== null);

  return maxBy(result, ({ processStatus }) =>
    parseInt(processStatus?.order, 10) || 0,
  );
};

/**
 * Resolves display status and navigation tracer for a project.
 * Falls back to API status fields and project.tracer when manual-tracer
 * matching fails (e.g. Web / external flows).
 */
export const resolveProjectStatus = (project, tracers = []) => {
  const statuses = get(project, "statuses", []);
  const matched = matchStatusesWithProcess(statuses, tracers);
  const rawStatus = getLatestProjectStatus(statuses);

  const processStatus = get(matched, "processStatus") || rawStatus;
  const tracer = get(matched, "tracer") || get(project, "tracer", null);

  return { processStatus, tracer };
};

export const canOpenProject = (project, tracers = []) => {
  const { tracer } = resolveProjectStatus(project, tracers);
  return !!get(project, "id") && !!get(tracer, "id");
};

export const isSignedLikeProject = (project, tracers = []) => {
  const { processStatus } = resolveProjectStatus(project, tracers);
  if (SIGNED_LIKE_PROCESS_TAGS.includes(processStatus?.tag)) {
    return true;
  }
  return get(project, "subscriptions", []).some(
    ({ status }) => status === SUBSCRIPTION_STATUSES.SIGNED,
  );
};
